import React, { useState, useEffect, useId } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type DatasetSearchAutoCompleteProps<T, U> = {
  data: T[];
  label?: string;
  searchFunction: (input: string, data: T[]) => T[] | string[] | U[];
  renderItem: (item: T | string | U) => React.ReactNode;
  getItemValue: (item: T | string | U) => string;
  onSelect: (item: T | string | U) => void;
  placeholder?: string;
  defaultValue?: T | U | string | null;
  onChange?: (input: string) => void;
};

function DatasetSearchAutoComplete<T, U>({
  data,
  label,
  searchFunction,
  renderItem,
  getItemValue,
  onSelect,
  placeholder,
  defaultValue,
  onChange,
}: DatasetSearchAutoCompleteProps<T, U>) {
  // Stable, collision-free id so the optional <Label> can be associated with the field
  // (WCAG 3.3.2 Labels or Instructions — A). Rendered several times per page, so a
  // hard-coded id would produce duplicates (4.1.1 Parsing — A).
  const inputId = useId();
  const [input, setInput] = useState("");
  const [filtered, setFiltered] = useState<T[] | string[] | U[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (defaultValue) {
      setInput(
        typeof defaultValue === "string"
          ? defaultValue
          : getItemValue(defaultValue),
      );
      setFiltered([]);
      setShowDropdown(false);
    } else {
      setInput("");
      setFiltered([]);
      setShowDropdown(false);
    }
  }, [defaultValue, getItemValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange?.(e.target.value);
    setInput(value);
    if (value.trim() === "") {
      setFiltered([]);
      setShowDropdown(false);
      return;
    }
    const results = searchFunction(value, data);
    setFiltered(results);
    setShowDropdown(results.length > 0);
  };

  const handleSelect = (item: T | U | string) => {
    const value = getItemValue(item);
    onSelect(item);
    setInput(value);
    setShowDropdown(false);
  };

  const clearInput = () => {
    onChange?.("");
    setInput("");
    setFiltered([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative my-2 w-full space-y-2">
      {/* 3.3.2 Labels or Instructions — A: the <Label> had no htmlFor and the field no id,
          so the label was orphaned and the field unnamed. */}
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Input
        id={inputId}
        aria-label={label ? undefined : (placeholder ?? "Search")}
        type="text"
        value={input}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full pr-10 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      {/* Clear/Search icon */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
        {input ? (
          // 2.1.1 Keyboard — A / 4.1.2 — A: the clear control was a bare Lucide <svg>
          // with an onClick — not focusable, no role, no name.
          <button
            type="button"
            onClick={clearInput}
            aria-label="Clear search"
            className="mt-6 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <X
              aria-hidden="true"
              className="size-4 cursor-pointer text-muted-foreground hover:text-foreground"
            />
          </button>
        ) : (
          <div className="mt-6 flex size-4 items-center justify-center text-muted-foreground">
            <Search />
          </div>
        )}
      </div>
      {showDropdown && (
        <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-[200px] w-full overflow-y-auto rounded-md border bg-white shadow-lg">
          {/* 2.1.1 Keyboard — A / 4.1.2 — A: results were <li onClick>, so a keyboard user
              could type a query but could not select any result it returned. */}
          {filtered.map((result, index) => (
            <li key={index}>
              <button
                type="button"
                className={`w-full cursor-pointer px-4 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                  index % 2 ? "bg-gray-50" : "bg-white"
                }`}
                onClick={() => handleSelect(result)}
              >
                {renderItem(result)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DatasetSearchAutoComplete;
